/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const createNumberValidation = (fieldName) =>
    Yup.number()
        .typeError(`Invalid data. '${fieldName}' must be a number`)
        .nullable(true)
        .test('is-number', `Invalid data. '${fieldName}' must be a number`, function (value) {
            return (value === null || value === undefined || !isNaN(value)) || this.createError({ message: `Invalid data. '${fieldName}' must be a number` });
        });


const lengthValidation = (fieldName, errorMessage) => Yup.number()
    .typeError(createNumberValidation(`Length - ${fieldName} (km)`))
    .nullable(true)
    .test('length-check', errorMessage, function (value) {
        const totalLength = this.resolve(Yup.ref('lengthTotal'));
        const sumOfLengths = [
            'lengthOpenTrack',
            'lengthTunnels',
            'infra_length_bridges'
        ].reduce((sum, field) => {
            const fieldValue = this.resolve(Yup.ref(field));
            return sum + (fieldValue || 0); 
        }, 0);

        if (sumOfLengths > totalLength) {
            throw this.createError({ message: errorMessage });
        }

        return true;
    });


    const validateCumulativeLevelCrossings = (errorMessage) => Yup.number()
    .typeError(createNumberValidation('Level crossings – total (no.)'))
    .nullable(true)
    .test('level-crossings-cumulative-check', errorMessage, function (value) {
        const totalLevelCrossings = value != null ? value : 0;
        const sumOfLevelCrossings = [
            'passiveLevelCross',
            'activeLevelCrossAutoUserWarn',
            'activeLevelCrossAutoUserProt',
            'activeLevelCrossRailsProt'
        ].reduce((sum, field) => {
            const fieldValue = this.resolve(Yup.ref(field));
            return sum + (fieldValue != null ? fieldValue : 0); // Treat undefined or null as 0
        }, 0);

        // Ensure the cumulative check is performed only when the total is greater than or equal to 0
        if (totalLevelCrossings >= 0 && sumOfLevelCrossings > totalLevelCrossings) {
            throw this.createError({ message: errorMessage });
        }

        return true;
    });

const TODIS_NRAIL_SCHEMA = Yup.object().shape({

    //general section Rules:
    general_road_id: Yup.string("Road ID is alphanumeric").nullable(true),
    startKm: Yup.number()
        .typeError("Invalid data. 'Start km' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'Start km\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'Start km\' must be a number' });
            }
            return true;
        }),
    endKm: Yup.number()
        .typeError("Invalid data. 'End km' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'End km\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'End km\' must be a number' });
            }
            return true;
        }),
    dateValidTo: Yup.date("Valid from is a Date").required().nullable(true),
    dateValidFrom: Yup.date("Valid to is a Date").required().nullable(true),
    dateValidFor: Yup.string("Valid for is a Year"),
    tenTCategory: Yup.string("tent category is alphanumeric").nullable(true),
    routeId: Yup.string("Route ID is alphanumeric").nullable(true),
    nationalRouteId: Yup.string("National Route ID is alphanumeric").nullable(true),
    internationalRouteId: Yup.string("International Route ID is alphanumeric").nullable(true),
    startNodeName: Yup.string("Start Node Name ID is alphanumeric").nullable(true),
    endNodeName: Yup.string("End Node Name is alphanumeric").nullable(true),
    nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
    addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
    contactNum: Yup.string()
        .transform((originalValue) => {
            // Si el valor comienza con '+', eliminamos ese símbolo
            return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
        })
        .nullable(true)
        .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
            // Permitir números, espacios, y la presencia de '+'
            const isValid = /^[0-9+\s]+$/.test(value) || value == null || value == undefined;

            if (!isValid) {
                throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
            }

            return true;
        }),

    contactEmail: Yup.string("Email")
        .transform((originalValue) => {
            // Realizar transformaciones si es necesario
            return typeof originalValue == 'string' ? originalValue.trim() : originalValue;
        })
        .nullable(true)
        .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
            // Si el valor es null, lo consideramos válido y no aplicamos más validaciones
            if (value == null) {
                return true;
            }

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

            for (const email of emails) {
                if (!emailRegex.test(email.trim())) {
                    throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
                }
            }

            return true;
        }),
    countryCode: Yup.string("Country Code").nullable(true),
    startKmDirA: Yup.number()
        .typeError("Invalid data. 'Start km (Direction A)' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'Start km (Direction A)\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'Start km (Direction A)\' must be a number' });
            }
            return true;
        }),
    startKmDirB: Yup.number()
        .typeError("Invalid data. 'Start km (Direction B)' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'Start km (Direction B)\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'Start km (Direction B)\' must be a number' });
            }
            return true;
        }),
    endKmDirA: Yup.number()
        .typeError("Invalid data. 'End km (Direction A)' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'End km (Direction A)\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'End km (Direction A)\' must be a number' });
            }
            return true;
        }),
    endKmDirB: Yup.number()
        .typeError("Invalid data. 'End km (Direction B)' must be a 'number'")
        .nullable(true)
        .test('is-number', 'Invalid data. \'End km (Direction B)\' must be a number', function (value) {
            if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                throw this.createError({ message: 'Invalid data. \'End km (Direction B)\' must be a number' });
            }
            return true;
        }),
    remarks: Yup.string("remarks").nullable(true),

    railwaysNpmEnvDTO: Yup.object().shape({
        co2Ems: Yup.number()
            .typeError("Invalid data. 'CO2 emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'CO2 emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'CO2 emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        noxEms: Yup.number()
            .typeError("Invalid data. 'NOx emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'NOx emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'NOx emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        so2Ems: Yup.number()
            .typeError("Invalid data. 'SO2 emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'SO2 emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'SO2 emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        nmhcEms: Yup.number()
            .typeError("Invalid data. 'Non-methane hydrocarbons (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Non-methane hydrocarbons (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Non-methane hydrocarbons (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        ppm: Yup.number()
            .typeError("Invalid data. 'Particulate matter (ppm)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Particulate matter (ppm)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Particulate matter (ppm)\' must be a number' });
                }
                return true;
            }),
        noise: Yup.number()
            .typeError("Invalid data. 'Noise (dB)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Noise (dB)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Noise (dB)\' must be a number' });
                }
                return true;
            }),
        chFloodInci: Yup.number()
            .typeError("Invalid data. 'Flooding incidents (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Flooding incidents (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Flooding incidents (no.)\' must be a number' });
                }
                return true;
            }),
        chClosureAdvWeatherCond: Yup.number()
            .typeError("Invalid data. 'Closures due to adverse weather conditions (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Closures due to adverse weather conditions (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Closures due to adverse weather conditions (no.)\' must be a number' });
                }
                return true;
            }),
        chEmbankFail: Yup.number()
            .typeError("Invalid data. 'Embankment failures (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Embankment failures (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Embankment failures (no.)\' must be a number' });
                }
                return true;
            }),
        chWinterMaintDays: Yup.number()
            .typeError("Invalid data. 'Winter maintenance days (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Winter maintenance days (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Winter maintenance days (no.)\' must be a number' });
                }
                return true;
            }),
        remarks: Yup.string("remarks").nullable(true),
    }),


    railwaysNpmInfraDTO: Yup.object().shape({
        capacity: Yup.number()
            .typeError("Invalid data. 'Capacity (trains/day)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Capacity (trains/day)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Capacity (trains/day)\' must be a number' });
                }
                return true;
            }),
        trackGauge: Yup.string('Value should be a text').nullable(true),
        loadGauge: Yup.string('Value should be a text').nullable(true),
        condTrack: Yup.string('Value should be a text').nullable(true),
        numTracks: Yup.number()
            .typeError("Invalid data. 'Number of tracks (total)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Number of tracks (total)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Number of tracks (total)\' must be a number' });
                }
                return true;
            }),
        traction: Yup.string('Value should be text').nullable(true),
        railVoltage: Yup.string('Value should be text')
            .when('traction', {
                is: 'Diesel',
                then: Yup.string().nullable(true).test('is-na', 'Rail voltage should be N/A for Diesel traction', function (value) {
                    return value === 'N/A';
                }),
                otherwise: Yup.string('Value should be text').nullable(true),
            }),
        lengthTotal: Yup.number()
            .typeError(createNumberValidation('Length – Total (km)'))
            .nullable(true),

        lengthOpenTrack: lengthValidation('Open Track', 'Length - Open Track should not be longer than the Total Length'),

        lengthTunnels: lengthValidation('Tunnels', 'Length - Tunnels should not be longer than the Total Length'),

        infra_length_bridges: lengthValidation('Bridges', 'Length - Bridges should not be longer than the Total Length'),

        levelCrossTotal: validateCumulativeLevelCrossings('Cumulated no. of level crossings per sub-categories should be equal to or lower than the total no. of level crossings'),

        passiveLevelCross: Yup.number()
            .typeError("Invalid data. 'Passive level crossings (no.)' must be a number")
            .nullable(true),

        activeLevelCrossAutoUserWarn: Yup.number()
            .typeError("Invalid data. 'Active level crossings - automatic with user-side warning (no.)' must be a number")
            .nullable(true),

        activeLevelCrossAutoUserProt: Yup.number()
            .typeError("Invalid data. 'Active level crossings – automatic with user-side protection (no.)' must be a number")
            .nullable(true),

        activeLevelCrossRailsProt: Yup.number()
            .typeError("Invalid data. 'Active level crossings – rail-side protected (no.)' must be a number")
            .nullable(true),

        maxDesignSpeed: Yup.number()
            .typeError("Invalid data. 'Maximum Design Speed (km/h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum Design Speed (km/h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum Design Speed (km/h)\' must be a number' });
                }
                return true;
            }),
        maxOperSpeed: Yup.number()
            .typeError("Invalid data. 'Maximum Operating Speed (km/h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum Operating Speed (km/h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum Operating Speed (km/h)\' must be a number' });
                }
                return true;
            }),
        maxLongGradDirA: Yup.number()
            .typeError("Invalid data. 'Maximum Longitudinal Gradient – Direction A (m per km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum Longitudinal Gradient – Direction A (m per km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum Longitudinal Gradient – Direction A (m per km)\' must be a number' });
                }
                return true;
            }),
        maxLongGradDirB: Yup.number()
            .typeError("Invalid data. 'Maximum Longitudinal Gradient – Direction B (m per km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum Longitudinal Gradient – Direction B (m per km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum Longitudinal Gradient – Direction B (m per km)\' must be a number' });
                }
                return true;
            }),
        minRadius: Yup.number()
            .typeError("Invalid data. 'Minimum radius (m)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Minimum radius (m)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Minimum radius (m)\' must be a number' });
                }
                return true;
            }),
        maxTrainLength: Yup.number()
            .typeError("Invalid data. 'Maximum train length (m)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum train length (m)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum train length (m)\' must be a number' });
                }
                return true;
            }),
        maxAxleLoad: Yup.number()
            .typeError("Invalid data. 'Maximum Axle load (kN)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maximum Axle load (kN)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maximum Axle load (kN)\' must be a number' });
                }
                return true;
            }),
        signStandard: Yup.string('Value should be a text').nullable(true),
        trafficManag: Yup.string('Value should be a text').nullable(true),
        ertms: Yup.string('Value should be text').nullable(true),
        ertmsLevel: Yup.string('Value should be text').nullable(true),
        contCommSys: Yup.string('Value should be text').nullable(true),
        loadingGauge5400: Yup.string('Value should be text').nullable(true),
        maxDwellingTimeCrossBorder25: Yup.string('Value should be text').nullable(true),
        maxDelayCrossBorder: Yup.string('Value should be a text').nullable(true),
        capacityBottlenecks: Yup.string('Value should be a text').nullable(true),
        remarks: Yup.string("remarks").nullable(true)
    }),
    railwaysNpmMaintDTO: Yup.object().shape({
        maintCostTotalKmpy: Yup.number()
            .typeError("Invalid data. 'Maintenance cost - total (Euro/km/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maintenance cost - total (Euro/km/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maintenance cost - total (Euro/km/year)\' must be a number' });
                }
                return true;
            }),
        maintCostOpenRoads: Yup.number()
            .typeError("Invalid data. 'Maintenance cost – Infrastructure (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maintenance cost – Infrastructure (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maintenance cost – Infrastructure (Euro/year)\' must be a number' });
                }
                return true;
            }),
        maintCostInfra: Yup.number()
            .typeError("Invalid data. 'Maintenance cost – Signalling and telecom system (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maintenance cost – Signalling and telecom system (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maintenance cost – Signalling and telecom system (Euro/year)\' must be a number' });
                }
                return true;
            }),
        maintCostSignTelecom: Yup.number()
            .typeError("Invalid data. 'Maintenance cost – Electrification system (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maintenance cost – Electrification system (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maintenance cost – Electrification system (Euro/year)\' must be a number' });
                }
                return true;
            }),
        maintCostElect: Yup.number()
            .typeError("Invalid data. 'Requiring heavy maintenance (km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Requiring heavy maintenance (km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Requiring heavy maintenance (km)\' must be a number' });
                }
                return true;
            }),
        financeSource: Yup.string('Value should be text').nullable(true),
        reqHeavyMaint: Yup.number()
            .typeError("Invalid data. 'Requiring rehabilitation (km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Requiring rehabilitation (km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Requiring rehabilitation (km)\' must be a number' });
                }
                return true;
            }),
        reqRehab: Yup.number()
            .typeError("Invalid data. 'Requiring upgrade to increase capacity (km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Requiring upgrade to increase capacity (km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Requiring upgrade to increase capacity (km)\' must be a number' });
                }
                return true;
            }),
        reqUpgradeIncCap: Yup.string('Value should be a text').nullable(true),
        reqUpgradeAddLine: Yup.number()
            .typeError("Invalid data. 'Requiring upgrade – additional track/new line (km)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Requiring upgrade – additional track/new line (km)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Requiring upgrade – additional track/new line (km)\' must be a number' });
                }
                return true;
            }),
        remarks: Yup.string("remarks").nullable(true),
    }),
    railwaysNpmOpsDTO: Yup.object().shape({
        passTrains: Yup.number()
            .typeError("Invalid data. 'Passenger Trains (no. per 24h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Passenger Trains (no. per 24h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Passenger Trains (no. per 24h)\' must be a number' });
                }
                return true;
            }),
        freightTrains: Yup.number()
            .typeError("Invalid data. 'Freight Trains (no./24h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight Trains (no./24h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight Trains (no./24h)\' must be a number' });
                }
                return true;
            }),
        dangerousGoodsFreightTrains: Yup.number()
            .typeError("Invalid data. 'Dangerous Goods Freight Trains (no./24h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Dangerous Goods Freight Trains (no./24h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Dangerous Goods Freight Trains (no./24h)\' must be a number' });
                }
                return true;
            }),
        capacityUsed: Yup.number()
            .typeError("Invalid data. 'Capacity used (% of capacity)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Capacity used (% of capacity)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Capacity used (% of capacity)\' must be a number' });
                }
                return true;
            }),
        passTrafficPy: Yup.number()
            .typeError("Invalid data. 'Passenger traffic (total no./year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Passenger traffic (total no./year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Passenger traffic (total no./year)\' must be a number' });
                }
                return true;
            }),
        passTrafficPassKmPy: Yup.number()
            .typeError("Invalid data. 'Passenger traffic (pass. km/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Passenger traffic (pass. km/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Passenger traffic (pass. km/year)\' must be a number' });
                }
                return true;
            }),
        passCommSpeed: Yup.number()
            .typeError("Invalid data. 'Commercial Speed for passenger traffic (km/h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Commercial Speed for passenger traffic (km/h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Commercial Speed for passenger traffic (km/h)\' must be a number' });
                }
                return true;
            }),
        freightTrafficTonsPy: Yup.number()
            .typeError("Invalid data. 'Freight traffic (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight traffic (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight traffic (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        freightTrafficTkmPy: Yup.number()
            .typeError("Invalid data. 'Freight traffic (tkm/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight traffic (tkm/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight traffic (tkm/year)\' must be a number' });
                }
                return true;
            }),
        freightCommSpeed: Yup.number()
            .typeError("Invalid data. 'Commercial speed for freight traffic (km/h)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Commercial speed for freight traffic (km/h)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Commercial speed for freight traffic (km/h)\' must be a number' });
                }
                return true;
            }),
        teusContaPy: Yup.number()
            .typeError("Invalid data. 'TEU containers per year (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'TEU containers per year (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'TEU containers per year (no.)\' must be a number' });
                }
                return true;
            }),
        unitised: Yup.number()
            .typeError("Invalid data. 'Unitised (% in standard loading units)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Unitised (% in standard loading units)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Unitised (% in standard loading units)\' must be a number' });
                }
                return true;
            }),
        nonUnitised: Yup.number()
            .typeError("Invalid data. 'Non Unitised (% in standard loading units)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Non Unitised (% in standard loading units)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Non Unitised (% in standard loading units)\' must be a number' });
                }
                return true;
            }),
        nationalTraffic: Yup.number()
            .typeError("Invalid data. 'National traffic (% of total traffic)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'National traffic (% of total traffic)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'National traffic (% of total traffic)\' must be a number' });
                }
                return true;
            }),
        aveTravelTimePass: Yup.number()
            .typeError("Invalid data. 'Average travel times passenger, incl. stops (long distance trains only)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Average travel times passenger, incl. stops (long distance trains only)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Average travel times passenger, incl. stops (long distance trains only)\' must be a number' });
                }
                return true;
            }),
        aveTravelTimeFreight: Yup.number()
            .typeError("Invalid data. 'Average travel time freight, incl. stops (long distance trains only)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Average travel time freight, incl. stops (long distance trains only)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Average travel time freight, incl. stops (long distance trains only)\' must be a number' });
                }
                return true;
            }),
        aveDelayedPass: Yup.number()
            .typeError("Invalid data. 'Average delay passenger trains (long distance trains only)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Average delay passenger trains (long distance trains only)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Average delay passenger trains (long distance trains only)\' must be a number' });
                }
                return true;
            }),
        aveDelayedFreight: Yup.number()
            .typeError("Invalid data. 'Average delay freight trains (long distance trains only)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Average delay freight trains (long distance trains only)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Average delay freight trains (long distance trains only)\' must be a number' });
                }
                return true;
            }),
        remarks: Yup.string("remarks").nullable(true),
    }),
    railwaysNpmSafetyDTO: Yup.object().shape({
        numIncidents: Yup.number()
            .typeError("Invalid data. 'Number of Incidents (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Number of Incidents (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Number of Incidents (no.)\' must be a number' });
                }
                return true;
            }),
        numAccidents: Yup.number()
            .typeError("Invalid data. 'Number of Accidents (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Number of Accidents (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Number of Accidents (no.)\' must be a number' });
                }
                return true;
            }),
        numSigniAccidents: Yup.number()
            .typeError("Invalid data. 'Number of Significant Accidents (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Number of Significant Accidents (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Number of Significant Accidents (no.)\' must be a number' });
                }
                return true;
            }),
        numSeriousAccidents: Yup.number()
            .typeError("Invalid data. 'Number of Serious Accidents (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Number of Serious Accidents (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Number of Serious Accidents (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentSerInj: Yup.number()
            .typeError("Invalid data. 'Serious Accidents - number of serious injuries (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Serious Accidents - number of serious injuries (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Serious Accidents - number of serious injuries (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentFatal: Yup.number()
            .typeError("Invalid data. 'Serious Accidents – number of fatalities (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Serious Accidents – number of fatalities (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Serious Accidents – number of fatalities (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentsOpenrail: Yup.number()
            .typeError("Invalid data. 'Serious Accidents – number per open rail (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Serious Accidents – number per open rail (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Serious Accidents – number per open rail (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentLevelcross: Yup.number()
            .typeError("Invalid data. 'erious Accidents – number per level crossings (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'erious Accidents – number per level crossings (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'erious Accidents – number per level crossings (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentStation: Yup.number()
            .typeError("Invalid data. 'Serious Accidents – number per station area (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Serious Accidents – number per station area (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Serious Accidents – number per station area (no.)\' must be a number' });
                }
                return true;
            }),
        serAccidentMatDamage: Yup.number()
            .typeError("Invalid data. 'Significant Accidents – amount of material damage (euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents – amount of material damage (euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents – amount of material damage (euro/year)\' must be a number' });
                }
                return true;
            }),
        serAccidentTrafficDisrupt: Yup.number()
            .typeError("Invalid data. 'Significant Accidents – disruption of traffic (hours per year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents – disruption of traffic (hours per year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents – disruption of traffic (hours per year)\' must be a number' });
                }
                return true;
            }),
        serAccidentIndDamageRel: Yup.number()
            .typeError("Invalid data. 'Significant Accidents - indirect damages related to delays (euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents - indirect damages related to delays (euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents - indirect damages related to delays (euro/year)\' must be a number' });
                }
                return true;
            }),
        signiInjAccidentNum: Yup.number()
            .typeError("Invalid data. 'Significant Accidents - number of Significant Injuries (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents - number of Significant Injuries (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents - number of Significant Injuries (no.)\' must be a number' });
                }
                return true;
            }),

        signiInjAccidentFatalNum: Yup.number()
            .typeError("Invalid data. 'Significant Accidents - number of Fatalities (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents - number of Fatalities (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents - number of Fatalities (no.)\' must be a number' });
                }
                return true;
            }),
        signiInjAccidentsOpenrail: Yup.number()
            .typeError("Invalid data. 'Significant Accidents - number per open rail (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents - number per open rail (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents - number per open rail (no.)\' must be a number' });
                }
                return true;
            }),
        signiInjAccidentLevelcross: Yup.number()
            .typeError("Invalid data. 'Significant Accidents- level crossings (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents- level crossings (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents- level crossings (no.)\' must be a number' });
                }
                return true;
            }),
        signiInjAccidentStation: Yup.number()
            .typeError("Invalid data. 'Significant Accidents - station areas (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Significant Accidents - station areas (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Significant Accidents - station areas (no.)\' must be a number' });
                }
                return true;
            }),


        remarks: Yup.string("remarks").nullable(true),
    }),
    railwaysNpmTentcompDTO: Yup.object().shape({
        electrification: Yup.boolean().nullable(true),
        rwTunnelComp: Yup.string().nullable(true),
        freightLineSpeed: Yup.string().nullable(true),
        /*         freightLineAxleLoad: Yup.boolean().nullable(true),
                freightLineTrainLength: Yup.boolean().nullable(true),
                trackGauge1435Mm: Yup.boolean().nullable(true), */
        ertmsDeployEtcs: Yup.boolean().nullable(true),
        ertmsDeployGsmr: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
    }),
    railwaysNpmClimateDTO: Yup.object().shape({
        climateEvents: Yup.array().of(
            Yup.object().shape({
                eventType: Yup.string()
                    .required("Event type is required")
                    .oneOf(["Flood", "Landslide", "Sea surge", "High temperature", "Snowdrift"], "Invalid event type"),
                startPointLat: Yup.number()
                    .typeError("Invalid data. 'Start Point Latitude' must be a number")
                    .nullable(true)
                    .min(-90, "Latitude must be between -90 and 90")
                    .max(90, "Latitude must be between -90 and 90"),
                startPointLon: Yup.number()
                    .typeError("Invalid data. 'Start Point Longitude' must be a number")
                    .nullable(true)
                    .min(-180, "Longitude must be between -180 and 180")
                    .max(180, "Longitude must be between -180 and 180"),
                endPointLat: Yup.number()
                    .typeError("Invalid data. 'End Point Latitude' must be a number")
                    .nullable(true)
                    .min(-90, "Latitude must be between -90 and 90")
                    .max(90, "Latitude must be between -90 and 90"),
                endPointLon: Yup.number()
                    .typeError("Invalid data. 'End Point Longitude' must be a number")
                    .nullable(true)
                    .min(-180, "Longitude must be between -180 and 180")
                    .max(180, "Longitude must be between -180 and 180"),
                disruptionStartDate: Yup.date()
                    .nullable(true)
                    .typeError("Invalid data. Disruption start date must be a valid date"),
                disruptionEndDate: Yup.date()
                    .nullable(true)
                    .typeError("Invalid data. Disruption end date must be a valid date")
                    .min(
                        Yup.ref('disruptionStartDate'),
                        "Disruption end date must be after start date"
                    )
            })
        ).nullable(true)
    }),
});




const NRAIL_INIT = {
    //general section Rules:
    general_road_id: "",
    startKm: 0,
    endKm: 0,
    dateValidTo: "",
    dateValidFrom: "",
    dateValidFor: "",
    tenTCategory: "",
    routeId: "",
    nationalRouteId: "",
    internationalRouteId: "",
    startNodeName: "",
    general_endnode_name: "",
    general_status: "",
    general_org_name: "",
    general_org_address: "",
    contactPer: "",
    contactPos: "",
    contactNum: 0,
    contactEmail: "",
    startKmDirA: "",
    startKmDirB: "",
    endKmDirA: "",
    endKmDirB: "",
    countryCode: "",
    remarks: "",

    railwaysNpmEnvDTO: {
        airPollutGhgTpy: "",
        co2Ems: "",
        noxEms: "",
        so2Ems: "",
        nmhcEms: "",
        ppm: "",
        noise: "",
        chFloodInci: "",
        chClosureAdvWeatherCond: "",
        chEmbankFail: "",
        chWinterMaintDays: "",
        remarks: ""
    },
    railwaysNpmInfraDTO: {
        capacity: "",
        trackGauge: "",
        loadGauge: "",
        condTrack: "",
        numTracks: "",
        traction: "",
        infra_traction_electrified: "",
        railVoltage: "",
        lengthTotal: "",
        lengthOpenTrack: "",
        lengthTunnels: "",
        infra_length_bridges: "",
        levelCrossTotal: "",
        passiveLevelCross: "",
        activeLevelCrossMan: "",
        activeLevelCrossAutoUserWarn: "",
        activeLevelCrossAutoUserProt: "",
        activeLevelCrossRailsProt: "",
        maxDesignSpeed: "",
        maxOperSpeed: "",
        maxLongGradDirA: "",
        maxLongGradDirB: "",
        minRadius: "",
        maxTrainLength: "",
        maxAxleLoad: "",
        signStandard: "",
        trafficManag: "",
        ertms: "",
        ertmsLevel: "",
        contCommSys: "",
        loadingGauge5400: "",
        maxDwellingTimeCrossBorder25: "",
        maxDelayCrossBorder: "",
        capacityBottlenecks: "",
        remarks: "",
    },
    railwaysNpmMaintDTO: {
        maintCostTotalKmpy: "",
        maintCostOpenRoads: "",
        maintCostInfra: "",
        maintCostSignTelecom: "",
        maintCostElect: "",
        financeSource: "",
        reqHeavyMaint: "",
        reqRehab: "",
        reqUpgradeIncCap: "",
        reqUpgradeAddLine: "",
        remarks: "",
    },
    railwaysNpmOpsDTO: {
        passTrains: "",
        freightTrains: "",
        dangerousGoodsFreightTrains: "",
        capacityUsed: "",
        passTrafficPy: "",
        passTrafficPassKmPy: "",
        passCommSpeed: "",
        freightTrafficTonsPy: "",
        freightTrafficTkmPy: "",
        freightCommSpeed: "",
        teusContaPy: "",
        unitised: "",
        nonUnitised: "",
        nationalTraffic: "",
        aveTravelTimePass: "",
        aveTravelTimeFreight: "",
        aveDelayedPass: "",
        aveDelayedFreight: "",
        remarks: "",
    },
    railwaysNpmSafetyDTO: {
        numIncidents: "",
        numAccidents: "",
        numSigniAccidents: "",
        numSeriousAccidents: "",
        serAccidentSerInj: "",
        serAccidentFatal: "",
        serAccidentsOpenrail: "",
        serAccidentLevelcross: "",
        serAccidentStation: "",
        serAccidentMatDamage: "",
        serAccidentTrafficDisrupt: "",
        serAccidentIndDamageRel: "",
        signiInjAccidentNum: "",
        signiInjAccidentsOpenrail: "",
        signiInjAccidentLevelcross: "",
        signiInjAccidentStation: "",
        remarks: "",
    },
    railwaysNpmTentcompDTO: {
        electrification: false,
        rwTunnelComp: false,
        freightLineSpeed: false,
        freightLineAxleLoad: false,
        freightLineTrainLength: false,
        trackGauge1435Mm: false,
        ertmsDeployEtcs: false,
        ertmsDeployGsmr: false,
        remarks: "",
        passengerDesignSpeed160: "false",
        loadingGauge: "false",
        maxDwellingTimeCrossBorder25: "false",
        strongInclines: "false",
    },
    railwaysNpmClimateDTO: {
        climateEvents: []
    },
}

export { TODIS_NRAIL_SCHEMA, NRAIL_INIT };